import { ChangeDetectionStrategy, Component, OnDestroy, effect, input, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { MusicDataService } from '../music-data.service';
import { FavouriteArtistsService } from '../favourite-artists.service';
import { FavouriteHeartComponent } from './favourite-heart.component';

/** "Related artists" for an artist, as links to their pages. Renders nothing if there are none. */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-related-artists',
  imports: [RouterLink, FavouriteHeartComponent],
  template: `
    @if (artists().length > 0) {
    <section class="related">
      <h2>Related artists{{artistName() ? ' to ' + artistName() : ''}}</h2>
      <div class="related-row">
        @for (a of artists(); track a.id) {
        <div class="related-item">
          <app-favourite-heart class="related-heart" size="small" [saved]="favourites.ids().has(a.id)" [name]="a.name"
            list="Favourite Artists" (toggled)="favourites.toggle(a.id, a.name)" />
          <a class="related-card" [routerLink]="['/artist', a.id]">
            <img [src]="a.images[1]?.url || a.images[0]?.url" [alt]="a.name" loading="lazy" />
            <span>{{a.name}}</span>
          </a>
        </div>
        }
      </div>
    </section>
    }
  `,
  styles: [`
    .related { margin: 24px 0; }
    .related-row { display: flex; gap: 14px; overflow-x: auto; padding: 4px 2px 12px; }
    .related-item { position: relative; flex-shrink: 0; }
    .related-heart { position: absolute; top: -2px; right: 0; z-index: 1; background: rgba(255,255,255,.85); border-radius: 50%; }
    .related-card { display: flex; flex-direction: column; align-items: center; gap: 6px; width: 104px; flex-shrink: 0;
      color: inherit; text-decoration: none; text-align: center; font-size: 0.85rem; }
    .related-card img { width: 88px; height: 88px; border-radius: 50%; object-fit: cover; box-shadow: 0 1px 4px rgba(0,0,0,.2); }
    .related-card:hover img { box-shadow: 0 4px 10px rgba(0,0,0,.3); }
    .related-card:hover span { text-decoration: underline; }
  `]
})
export class RelatedArtistsComponent implements OnDestroy {
  artistId = input<string | null | undefined>(null);
  artistName = input('');

  artists = signal<any[]>([]);
  private sub?: Subscription;

  constructor(private data: MusicDataService, protected favourites: FavouriteArtistsService) {
    this.favourites.ensureIds();
    effect(() => {
      const id = this.artistId();
      this.sub?.unsubscribe();
      this.artists.set([]);
      if (!id) return;
      this.sub = this.data.getRelatedArtists(String(id)).subscribe({
        next: (artists) => this.artists.set(artists.filter(a => String(a.id) !== String(id))),
        error: () => this.artists.set([]) // the section is optional, so just leave it out
      });
    });
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }
}
